// Support ticket service
// ------------------------
// Submits an in-app support ticket via the authenticated AppSync GraphQL API.
//
// The reporter's name/email are deliberately NOT sent from the client — the
// backend derives the reporter's identity from the Cognito session (see
// handlers/supportdatasource.py), so a ticket is always attributable and cannot
// be spoofed. The client only sends the category, message, and a light context
// breadcrumb.
//
// The mutation string lives here rather than in services/graphql/mutations.ts
// because that file is codegen output ("auto generated ... will be overwritten");
// keeping the support mutation local prevents it being clobbered and co-locates
// the feature's data access.

import { API, graphqlOperation as _graphqlOperation } from "aws-amplify";
import { assertNoTenantIdInVariables } from "@/services/graphql/assertNoTenantId";
import { getGraphData } from "@/utils/transform";

// Categories MUST match the SupportCategory enum in the backend GraphQL schema
// (schema/client_appsync.graphql). Labels are display-only.
export const SUPPORT_CATEGORIES = [
  { value: "QUESTION", label: "Question or how-to" },
  { value: "ISSUE", label: "Issue" },
  { value: "FEATURE_REQUEST", label: "Feature request" },
  { value: "BILLING", label: "Billing" },
  { value: "ACCOUNT_ACCESS", label: "Account and access" },
  { value: "OTHER", label: "Other" },
] as const;

export type SupportCategory = (typeof SUPPORT_CATEGORIES)[number]["value"];

// Mirror the backend bounds so the user gets immediate feedback; the server
// re-validates authoritatively.
export const SUPPORT_MESSAGE_MIN_LENGTH = 5;
export const SUPPORT_MESSAGE_MAX_LENGTH = 2000;

const submitSupportTicketMutation = `mutation SubmitSupportTicket($input: SubmitSupportTicketInput!) {
  submitSupportTicket(input: $input) {
    ok
    id
    message
  }
}
`;

// Force Cognito auth on the call — identity is derived server-side from the JWT.
const graphqlOperation = (query: any, variables?: any) => {
  assertNoTenantIdInVariables(variables);
  return {
    ..._graphqlOperation(query, variables),
    authMode: "AMAZON_COGNITO_USER_POOLS" as const,
  };
};

export interface SubmitSupportTicketInput {
  category: SupportCategory;
  message: string;
  appContext?: string;
}

export interface SubmitSupportTicketResult {
  ok: boolean;
  id?: string | null;
  message?: string | null;
}

// Submit a support ticket. Never throws — always resolves to a result object so
// the caller can render success/failure inline.
export async function submitSupportTicket(
  input: SubmitSupportTicketInput
): Promise<SubmitSupportTicketResult> {
  try {
    const response: any = await API.graphql(
      graphqlOperation(submitSupportTicketMutation, { input })
    );
    const data = getGraphData(response, "submitSupportTicket") as SubmitSupportTicketResult | null;
    if (!data) {
      return { ok: false, message: "Could not submit your ticket. Please try again." };
    }
    return data;
  } catch (submitError: any) {
    const detail = submitError?.errors?.[0]?.message || submitError?.message;
    console.error(`submitSupportTicket failed: ${detail}`);
    return { ok: false, message: "Could not submit your ticket. Please try again." };
  }
}
